import type { TokenPair } from '@contracts/auth'
import { isApiError, type RenewResult } from '@/shared/api'
import { createLocalId } from '@/shared/lib/id'
import { safeStorage, type SafeStorage } from '@/shared/lib/safe-storage'
import type { TokenStore } from './token-store'

/** Runs one task at a time across the tabs of this browser. */
export type RunExclusive = <T>(task: () => Promise<T>) => Promise<T>

export interface SessionRenewer {
  /** Concurrent callers share one renewal. */
  renew(): Promise<RenewResult>
}

interface SessionRenewerOptions {
  tokens: TokenStore
  /** Exchanges a refresh token for a new pair; rejects with an ApiError. */
  exchange: (refreshToken: string) => Promise<TokenPair>
  runExclusive: RunExclusive
}

/**
 * The only caller of the token exchange (ADR-0009). A refresh token works once and presenting it
 * twice makes the backend end the whole sign-in, so there is one renewal at a time: per tab through
 * the shared promise, across tabs through `runExclusive`.
 */
export function createSessionRenewer(options: SessionRenewerOptions): SessionRenewer {
  const { tokens, exchange, runExclusive } = options
  let inFlight: Promise<RenewResult> | null = null

  async function attempt(): Promise<RenewResult> {
    // Read inside the lock: another tab may have renewed while this one waited.
    const refreshToken = tokens.getRefreshToken()
    if (refreshToken === null) {
      tokens.clear()
      return { outcome: 'ended' }
    }
    try {
      tokens.set(await exchange(refreshToken))
      return { outcome: 'renewed' }
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        tokens.clear()
        return { outcome: 'ended' }
      }
      // Unreachable backend, timeout or server error: the session may still be valid.
      return { outcome: 'failed', error }
    }
  }

  return {
    renew() {
      inFlight ??= runExclusive(attempt).finally(() => {
        inFlight = null
      })
      return inFlight
    },
  }
}

export const RENEW_LOCK_NAME = 'app:auth-refresh'
export const RENEW_LEASE_KEY = 'app:auth-refresh-lock'

/** Web Locks where available; they need a secure context, so plain HTTP falls back to a lease. */
export function createRenewLock(storage: SafeStorage = safeStorage('local')): RunExclusive {
  const locks = (globalThis.navigator as Partial<Navigator> | undefined)?.locks
  if (!locks) return createLeaseLock(storage)
  return <T>(task: () => Promise<T>) =>
    new Promise<T>((resolve, reject) => {
      void locks.request(RENEW_LOCK_NAME, () => task().then(resolve, reject))
    })
}

interface LeaseLockOptions {
  /** Longer than a renewal can take; a lease left by a closed tab expires after it. */
  leaseMs?: number
  /** Wait between writing the lease and checking who holds it. */
  settleMs?: number
  pollMs?: number
  now?: () => number
  sleep?: (ms: number) => Promise<void>
}

/**
 * Best-effort lock for browsers without Web Locks: a lease in localStorage, written, then read back
 * after a short wait. Two tabs can still both win within that wait; the backend then ends the
 * sign-in and the user signs in again.
 */
export function createLeaseLock(
  storage: SafeStorage,
  options: LeaseLockOptions = {},
): RunExclusive {
  const {
    leaseMs = 20_000,
    settleMs = 50,
    pollMs = 100,
    now = Date.now,
    sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  } = options
  const owner = createLocalId('tab')

  const holder = (): { owner: string; expiresAt: number } | null => {
    try {
      const lease = JSON.parse(storage.get(RENEW_LEASE_KEY) ?? 'null') as {
        owner?: unknown
        expiresAt?: unknown
      } | null
      if (typeof lease?.owner !== 'string' || typeof lease.expiresAt !== 'number') return null
      return lease.expiresAt > now() ? { owner: lease.owner, expiresAt: lease.expiresAt } : null
    } catch {
      return null
    }
  }

  async function acquire(): Promise<void> {
    for (;;) {
      if (holder() === null) {
        storage.set(RENEW_LEASE_KEY, JSON.stringify({ owner, expiresAt: now() + leaseMs }))
        await sleep(settleMs)
        const current = holder()
        // No holder after writing: storage is unavailable, so there is no other tab to wait for.
        if (current === null || current.owner === owner) return
      }
      await sleep(pollMs)
    }
  }

  return async (task) => {
    await acquire()
    try {
      return await task()
    } finally {
      if (holder()?.owner === owner) storage.remove(RENEW_LEASE_KEY)
    }
  }
}
